import React, { useEffect, useMemo, useState } from 'react';
import { Input, Segmented, Badge, Tag, Empty, Spin, Button, DatePicker, message } from 'antd';
import { SearchOutlined, PhoneOutlined, FilterOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { DriverFeedbackItem } from '../types/driverFeedback';
import { LinkStatus } from '../types/pickupSite';
import {
  GET_DRIVER_FEEDBACK_LISTING,
  GetDriverFeedbackListingResponse,
} from '../graphql/queries/getDriverFeedbackListing';
import { toDriverFeedbackItem, resolveFollowUpContact } from '../utils/driverFeedbackAdapter';
import { hubDayjs, useHubZone } from '../utils/hubTime';

interface Props {
  /** 当前 hub（warehouse_id）；无则不查询 */
  hubId?: number;
  /** 外部动作后 +1 触发刷新 */
  reloadToken?: number;
  selectedId?: number;
  onSelect?: (item: DriverFeedbackItem) => void;
  /** 回传待跟进总数（供页面 Tab 徽标） */
  onCountChange?: (total: number) => void;
  /**
   * 回传「有新司机反馈」的条数（hasNewFeedback = 有 op4 上门日志且 link_status∈{2揽收成功待建联,3揽收失败待建联}）。
   * 顶部提醒专用：文案是「收到了司机上门反馈，需要跟进处理」，待上门/上门中的站点还没有反馈结果，不该计入。
   */
  onNewFeedbackCountChange?: (count: number) => void;
  /**
   * 选中项已不在列表里 → 通知页面清空选中，让详情栏回到初始态。
   *
   * <p>只在 resolve 回调里清不够：任何「没走 resolve 但卡片从列表消失」的路径都会残留陈旧详情
   * （如点「安排司机上门」跳到预约单页再切回来）。以列表数据为准做校验，一次覆盖所有路径，
   * 不必每加一个操作都补一次清理。
   */
  onSelectedMissing?: () => void;
}

const { RangePicker } = DatePicker;

/** 跟进列表筛选 = 全部 + link_status∈{1,2,3,5}（技术方案 §172）。 */
type StatusFilter = 'all' | LinkStatus;

/**
 * link_status → 短标签 i18n key（揽收成功/揽收失败/确认无法建联）。
 *
 * 只给「完整文案过长、卡片与筛选放不下」的状态配短标签，其余交给 lsLabel 回落到
 * linkStatus.<值>，保证与站点管理页同一口径。「待上门」曾配过短标签「未上门」，
 * 但两者都是 3 字、无缩短收益，只是让同一状态在两个页面显示成两个词（英文/西语同样
 * Pending Visit vs Not visited），故移除、统一回落。
 */
const LS_SHORT_KEY: Partial<Record<LinkStatus, string>> = {
  [LinkStatus.PickedUpPending]: 'filterSuccess',
  [LinkStatus.PickupFailedPending]: 'filterFailed',
  [LinkStatus.Unreachable]: 'filterUnreachable',
};

/** link_status → 状态 Tag 配色。 */
const LS_COLOR: Partial<Record<LinkStatus, string>> = {
  [LinkStatus.Pending]: 'orange',
  // 上门中：已派单在途，用紫色与「待上门(橙)」区分，避免运营误判为还没安排
  [LinkStatus.OnsiteInProgress]: 'purple',
  [LinkStatus.PickedUpPending]: 'blue',
  [LinkStatus.PickupFailedPending]: 'red',
  [LinkStatus.Unreachable]: 'default',
};

/**
 * 列表卡片时间：hub 时区下的 YYYY-MM-DD HH:mm。
 *
 * 刻意不复用 formatHubTimeShort（MM-DD HH:mm）—— 那会把年份去掉，是可见的展示变更；
 * 本次只修时区，不动格式。与详情侧的 formatHubTimeFull 差一个秒位，也是刻意的：
 * 列表看的是"哪天进来的"，详情要能分辨"两步操作差了几秒"。
 */
function fmt(iso: string, hubZone?: string): string {
  return hubDayjs(iso, hubZone)?.format('YYYY-MM-DD HH:mm') ?? iso;
}

/**
 * 司机反馈跟进 · 列表（P6.7）
 * 搜索 + 建联状态筛选（全部/未上门/揽收成功/揽收失败/确认无法建联，口径 link_status∈{1,2,3,5}）。
 * 卡片对齐 PRD：地址标题 + 状态Tag + 站点ID + 联系人/电话 + 渠道 + 创建时间。数据来自后端。
 */
const DriverFeedbackList: React.FC<Props> = ({
  hubId,
  reloadToken,
  selectedId,
  onSelect,
  onCountChange,
  onNewFeedbackCountChange,
  onSelectedMissing,
}) => {
  const { t } = useTranslation();
  const hubZone = useHubZone();
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [showMore, setShowMore] = useState(false);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const handleToggleMore = () => {
    const next = !showMore;
    setShowMore(next);
    if (!next) setRange(null); // 收起时清空（对齐新地址审核）
  };

  const lsLabel = (ls: LinkStatus): string =>
    LS_SHORT_KEY[ls]
      ? (t(`pickup.addressReviewPool.driverFeedback.${LS_SHORT_KEY[ls]}`) as string)
      : (t(`pickup.addressReviewPool.linkStatus.${ls}`) as string);

  const { data, loading, refetch, error } = useQuery<GetDriverFeedbackListingResponse>(GET_DRIVER_FEEDBACK_LISTING, {
    variables: { hubId: hubId as number, pageNo: 1, pageSize: 200 },
    skip: hubId == null,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (reloadToken && hubId != null) {
      refetch();
    }
  }, [reloadToken]);

  // useQuery 的 error 此前没人读：取数失败时 data 为 undefined ⇒ items 为空 ⇒ 渲染成
  // 「暂无待跟进站点」。运营看到的是"今天没活"，不是"加载失败"，于是不会报障。
  // 典型触发：desktop-web 已发新版、api-server 还是旧的（或被单独回滚），查询里的四个
  // *Count 字段不在 schema 里 ⇒ GraphQL 整条校验失败。
  // 这里不需要像新地址审核那侧那样加「停止重试」的闸 —— 本列表是单次 useQuery，没有链式翻页。
  useEffect(() => {
    if (!error) return;
    console.error('driver feedback listing failed', error);
    message.error(t('pickup.addressReviewPool.list.loadFailed'));
    // 刻意只依赖 error：把 t 放进来，切换语言时会对着同一个旧错误再弹一次 toast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const items = useMemo<DriverFeedbackItem[]>(
    () => (data?.getDriverFeedbackListing?.items ?? []).map((d) => toDriverFeedbackItem(d, hubId as number)),
    [data, hubId],
  );

  // 回传总数（优先后端 total）供页面 Tab 徽标
  useEffect(() => {
    if (data?.getDriverFeedbackListing) {
      onCountChange?.(data.getDriverFeedbackListing.total ?? items.length);
    }
  }, [data]);

  // 回传「有新司机反馈」条数供顶部提醒。后端未提供该维度的 total，只能按已取回的 items 数（pageSize 200）统计；
  // 待跟进量超过 200 时会少算，届时需后端在 listing 上补一个 newFeedbackTotal
  useEffect(() => {
    if (data?.getDriverFeedbackListing) {
      onNewFeedbackCountChange?.(items.filter((it) => it.hasNewFeedback).length);
    }
  }, [data, items]);

  // 选中项已从列表消失（被处理掉/状态流转出跟进口径）→ 通知页面清空选中，详情栏回初始态。
  // 依赖 data 而非 items：只在真实拉到新数据时校验，避免 loading 中途误清。
  useEffect(() => {
    if (!data?.getDriverFeedbackListing || selectedId == null) return;
    if (!items.some((it) => it.id === selectedId)) {
      onSelectedMissing?.();
    }
  }, [data, items, selectedId, onSelectedMissing]);

  // 头部计数：待跟进总数（后端 total）+ 今日新增（后端 todayNewCount）
  const pendingTotal = data?.getDriverFeedbackListing?.total ?? items.length;
  const todayNewCount = data?.getDriverFeedbackListing?.todayNewCount ?? 0;

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return items.filter((it) => {
      if (status !== 'all' && it.linkStatus !== status) return false;
      if (kw) {
        const hit =
          it.displayAddress.toLowerCase().includes(kw) ||
          it.siteName.toLowerCase().includes(kw) ||
          (it.onSiteContact?.phone || '').includes(kw);
        if (!hit) return false;
      }
      if (range && (range[0] || range[1])) {
        const ts = it.siteCreatedAt ? new Date(it.siteCreatedAt).getTime() : NaN;
        if (range[0] && (Number.isNaN(ts) || ts < range[0].startOf('day').valueOf())) return false;
        if (range[1] && (Number.isNaN(ts) || ts > range[1].endOf('day').valueOf())) return false;
      }
      return true;
    });
  }, [items, keyword, status, range]);

  // 打开/切回本 tab 时默认选中第一条，避免右侧长期停留空态。
  // 用 filtered（筛选后所见的第一条）；仅在当前无选中时触发，不打断运营已选的条目。
  useEffect(() => {
    if (selectedId == null && filtered.length > 0) {
      onSelect?.(filtered[0]);
    }
  }, [filtered, selectedId, onSelect]);

  // 产品确认：不出「确认无法建联」筛选项（终态，运营不需要按它筛）。
  // 注意后端 FOLLOW_UP_LINK_STATUSES 仍含 5，这类站点依然会出现在「全部」里。

  /**
   * 2.2 需求点6：建联状态页签带数量。
   *
   * 四个数**来自后端**（与 total 同过滤），不能在前端按 items 数 —— 本接口 pageSize 固定 200，
   * 待跟进超过 200 时就会少算（与下方 hasNewFeedback 统计是同一个坑）。
   * 「全部」刻意不带：它的数就是上方那行的「待跟进 N」（产品 2026-10-14 定）。
   */
  const listing = data?.getDriverFeedbackListing;
  const withCount = (label: React.ReactNode, n: number) => (
    <span>
      {label}
      {/* text-xs(12px) 不是纯装饰：角标跟页签文案同为 14px 时，光是一位数就要 422px、
          容器只有 420px ⇒ **任何 hub 都常驻一条横向滚动条**。降到 12px 后，一位数到
          EWR 真实量级(14/1/2/5)都不触发滚动，上面那个 overflow-x-auto 才退回成只兜极端情况。
          2026-10-14 测试环境实测：14px→422px(滚动)，12px→413px / 真实数据 416px(不滚动)。 */}
      <span className="ml-0.5 text-xs text-gray-400">({n})</span>
    </span>
  );

  const filterOptions = [
    { label: t('pickup.addressReviewPool.driverFeedback.filterAll'), value: 'all' as const },
    {
      label: withCount(lsLabel(LinkStatus.Pending), listing?.pendingOnsiteCount ?? 0),
      value: LinkStatus.Pending,
    },
    {
      label: withCount(
        lsLabel(LinkStatus.OnsiteInProgress),
        listing?.onsiteInProgressCount ?? 0,
      ),
      value: LinkStatus.OnsiteInProgress,
    },
    {
      label: withCount(
        lsLabel(LinkStatus.PickedUpPending),
        listing?.pickupSuccessPendingCount ?? 0,
      ),
      value: LinkStatus.PickedUpPending,
    },
    {
      label: withCount(
        lsLabel(LinkStatus.PickupFailedPending),
        listing?.pickupFailedPendingCount ?? 0,
      ),
      value: LinkStatus.PickupFailedPending,
    },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* 标题行：📞 待跟进站点 [待跟进 N]　今日新增 M */}
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-purple-50">📞</span>
        <span className="text-[15px] font-semibold text-gray-800">
          {t('pickup.addressReviewPool.driverFeedback.pendingTitle')}
        </span>
        <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-600">
          {t('pickup.addressReviewPool.driverFeedback.pendingLabel')} {pendingTotal}
        </span>
        <span className="ml-auto text-xs text-gray-400">
          {t('pickup.addressReviewPool.list.todayNew')} {todayNewCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Input
          allowClear
          className="flex-1"
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder={t('pickup.addressReviewPool.driverFeedback.searchPlaceholder') as string}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Button icon={<FilterOutlined />} type={showMore ? 'primary' : 'default'} onClick={handleToggleMore}>
          {t('pickup.addressReviewPool.list.moreFilter')}
        </Button>
      </div>
      {/* overflow-x-auto 不能省：这一行有 5 个页签、文案又长（「揽收成功」「揽收失败」），
          2.2 需求点6 给每项加上角标后就装不下了。左栏是**固定宽度**，所以 1920×1080 和
          1366×768 一样会炸 —— 别以为宽屏就没事（2026-10-14 测试环境实测，两档重叠量完全相同）。
          可用宽度 436px：四项角标都到两位数就溢出 22px，最后一个页签被右侧详情面板盖住；
          生产量级（EWR 待跟进 99+）必然命中。缩字号、缩文案都不够：4 位数时仍分别超 59 / 28px。 */}
      <div className="mt-2 overflow-x-auto">
        <Segmented options={filterOptions as any} value={status} onChange={(v) => setStatus(v as StatusFilter)} />
      </div>
      {showMore && (
        <div className="mt-2">
          <RangePicker
            className="w-full"
            value={range as any}
            onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
            placeholder={[
              t('pickup.addressReviewPool.list.dateRange') as string,
              t('pickup.addressReviewPool.list.dateRange') as string,
            ]}
          />
        </div>
      )}

      <div className="mt-3 flex-1 overflow-auto pr-1">
        {loading ? (
          <div className="mt-24 flex justify-center">
            <Spin />
          </div>
        ) : filtered.length === 0 ? (
          <Empty description={t('pickup.addressReviewPool.driverFeedback.empty')} className="mt-24" />
        ) : (
          filtered.map((it) => {
            const selected = it.id === selectedId;
            return (
              <div
                key={it.id}
                onClick={() => onSelect?.(it)}
                className={`mb-3 cursor-pointer rounded-lg border p-3 transition-colors ${
                  selected ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                {/* 地址标题 + 状态 Tag */}
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-gray-800">
                    {it.hasNewFeedback && <Badge status="error" className="!mr-1" />}
                    {it.displayAddress}
                  </div>
                  <Tag color={LS_COLOR[it.linkStatus] || 'default'} className="!m-0 shrink-0">
                    {lsLabel(it.linkStatus)}
                  </Tag>
                </div>
                {/* 站点 ID */}
                <div className="mt-1">
                  <span className="rounded-md bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-600">
                    {t('pickup.addressReviewPool.list.stationId')}: {it.siteId}
                  </span>
                </div>
                {/* 联系人 / 电话：与右侧详情同口径（现场回传优先、回退站点联系人），
                    否则「未上门」阶段卡片显示 - 而详情有值 */}
                {(() => {
                  const { contact } = resolveFollowUpContact(it);
                  return (
                    <div className="mt-1.5 flex items-center gap-4 text-sm text-gray-700">
                      <span className="flex items-center gap-1">👤 {contact?.name || '-'}</span>
                      {contact?.phone && (
                        <span className="flex items-center gap-1">
                          <PhoneOutlined />
                          {contact.phone}
                        </span>
                      )}
                    </div>
                  );
                })()}
                {/* 渠道 */}
                <div className="mt-1 flex items-center gap-1 text-sm text-gray-600">
                  🛍 <span>{it.channelNames.join('、') || '-'}</span>
                </div>
                {/* 分隔 + 创建时间 */}
                <div className="my-2 border-t border-gray-100" />
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  🕐 {t('pickup.addressReviewPool.list.createTime')} {fmt(it.siteCreatedAt || it.feedbackTime, hubZone)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DriverFeedbackList;
